import React, { useEffect, useState } from "react";

const AboutUs = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const containerStyle = {
    padding: isMobile ? "22px 14px 34px" : "36px 22px 48px",
    maxWidth: "980px",
    margin: "auto",
    fontFamily: "'Arial', sans-serif",
    lineHeight: "1.7",
    color: "#333",
  };

  const heroStyle = {
    background: "linear-gradient(135deg, #f6f9fc 0%, #eef3f7 100%)",
    border: "1px solid #e6ecf1",
    borderRadius: "16px",
    padding: isMobile ? "22px 14px" : "34px 24px",
    marginBottom: "28px",
    textAlign: "center",
  };

  const sectionStyle = {
    marginBottom: "22px",
  };

  const headingStyle = {
    marginBottom: "10px",
    color: "#2c3e50",
    fontWeight: "bold",
    letterSpacing: "0.2px",
  };

  const listStyle = {
    margin: "0 0 0 18px",
    padding: 0,
    lineHeight: "1.7",
  };

  const cardSectionStyle = {
    border: "1px solid #e8edf2",
    borderRadius: "14px",
    backgroundColor: "#fff",
    padding: isMobile ? "14px" : "18px",
    boxShadow: "0 6px 16px rgba(44, 62, 80, 0.05)",
    marginBottom: "16px",
  };

  const statsGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
    gap: "12px",
    marginBottom: "24px",
  };

  const statCardStyle = {
    border: "1px solid #e8edf2",
    borderRadius: "12px",
    padding: "14px",
    backgroundColor: "#ffffff",
    boxShadow: "0 4px 12px rgba(44, 62, 80, 0.04)",
  };

  const statNumberStyle = {
    display: "block",
    color: "#2c3e50",
    fontSize: "24px",
    fontWeight: 700,
    marginBottom: "6px",
  };

  const emphasisStyle = {
    display: "inline-block",
    fontWeight: 700,
    color: "#b42318",
    backgroundColor: "#fff4f2",
    border: "1px solid #ffd5ce",
    borderRadius: "8px",
    padding: "1px 8px",
  };

  return (
    <div style={containerStyle}>
      <section style={heroStyle}>
        <h1 style={{ margin: "0 0 8px", color: "#2c3e50", fontSize: isMobile ? "28px" : "36px" }}>
          About Rate My Courses
        </h1>
        <p style={{ margin: 0, maxWidth: "760px", marginInline: "auto", color: "#516171" }}>
          We help students make smarter course decisions with real, student-powered insights.
        </p>
      </section>

      <section style={statsGridStyle}>
        <article style={statCardStyle}>
          <span style={statNumberStyle}>1 Platform</span>
          <p style={{ margin: 0, color: "#5c6b77" }}>
            A centralized space for course experiences and guidance.
          </p>
        </article>
        <article style={statCardStyle}>
          <span style={statNumberStyle}>10x Cost</span>
          <p style={{ margin: 0, color: "#5c6b77" }}>
            International students often face much higher tuition risk.
          </p>
        </article>
        <article style={statCardStyle}>
          <span style={statNumberStyle}>1 Mission</span>
          <p style={{ margin: 0, color: "#5c6b77" }}>
            Reduce stress and improve outcomes through better course choices.
          </p>
        </article>
      </section>

      <section style={sectionStyle}>
        <h2 style={headingStyle}>What Is Rate My Courses?</h2>
        <p>
          <strong>Rate My Courses</strong> is a course rating and review platform for university students. 
          It’s a space where students can share honest reviews, offer advice, and make course selection easier for everyone. 
          Even though some of us have already graduated, we want to ensure that future students don’t face the same challenges we did.
        </p>
      </section>

      <section style={{ ...sectionStyle, ...cardSectionStyle }}>
        <h2 style={headingStyle}>The Student Struggle</h2>
        <p>
          Choosing the right courses is one of the most important decisions students make throughout their degree. 
          It directly impacts their GPA, career prospects, and self-confidence. The wrong choice can lead to stress, self-doubt, 
          and how others — peers, professors, and future employers — perceive them.
        </p>
        <p>
          At large universities, students have a vast selection of courses to choose from. But with greater flexibility comes greater stress; 
          more choices mean more uncertainty. This burden is heavier for <span style={emphasisStyle}>international students who pay 10 times more</span> 
          than domestic students (at UofT, international students pay $60,000 a year). Dropping a course is a luxury they simply cannot afford—forcing them to stay in courses 
          that may harm their GPA, mental health, and overall university experience.
        </p>
      </section>

      <section style={{ ...sectionStyle, ...cardSectionStyle }}>
        <h2 style={headingStyle}>The Problem</h2>
        <p>Course selection is too time-consuming and stressful for students.</p>
        <ul style={listStyle}>
          <li>
            <strong>Too many options, not enough guidance:</strong>
            <ul>
              <li>Course syllabi and academic advisors provide little to no guidance on course difficulty, teaching style, workload, etc.</li>
              <li>Course content isn’t publicly available, making it difficult to gauge whether a course is the right fit.</li>
              <li>Not everyone has access to upper-year students and may be too shy or intimidated to reach out.</li>
            </ul>
          </li>
          <li><strong>No centralized source of student reviews:</strong> Students rely on group chats and scattered forums for advice.</li>
          <li><strong>Expectation vs Reality:</strong> What may seem interesting and easy at the start of the semester can completely change in a matter of weeks.</li>
          <li><strong>Not enough time to decide:</strong> Refund deadlines don’t leave enough time to decide whether to stay in a course.</li>
          <li><strong>Dropping a course is expensive:</strong> Dropping courses beyond the full refund deadline (which is two weeks after the semester starts) can cost thousands of dollars.</li>
          <li>Being stuck in the wrong course can severely affect your GPA, mental health, and future prospects.</li>
        </ul>
      </section>

      <section style={{ ...sectionStyle, ...cardSectionStyle }}>
        <h2 style={headingStyle}>Our Mission</h2>
        <p>To help students make informed course-related decisions, thereby improving their mental health and academic performance.</p>
        <ul style={listStyle}>
          <li><strong>Improve Student Mental Health:</strong> We reduce course-related stress with honest, student-generated reviews.</li>
          <li><strong>Enhance Academic Success:</strong> By providing insights from past students, we help students make informed decisions, enter courses with confidence, and set themselves up for success.</li>
          <li><strong>Lower Regret & Course Drop Rates:</strong> We minimize drop rates and improve academic satisfaction.</li>
          <li><strong>Save Time:</strong> We eliminate hours of scattered research and outreach by offering a centralized source of course insights.</li>
          <li><strong>Save Money:</strong> We help students—especially international students—avoid costly course drops that can amount to thousands of dollars.</li>
          <li><strong>Promote Equity </strong> We level the academic playing field by giving all students— regardless of status, year of study, or access to networks— equal access to the knowledge they need to succeed. </li>  
        </ul>
      </section>

      <section style={{ ...sectionStyle, ...cardSectionStyle }}>
        <h2 style={headingStyle}>Our Vision</h2>
        <p>To improve the student experience for university students across North America.</p>
      </section>

      <section style={{ ...sectionStyle, ...cardSectionStyle }}>
        <h2 style={headingStyle}>Join Us Today!</h2>
        <p>Share your course reviews and help others make smarter, stress-free academic choices.</p>
      </section>
    </div>
  );
};

export default AboutUs;
